import { Injectable } from '@angular/core';

import {
  ACCESS_TOKEN,
  CURRENT_TIMEZONE,
  CURRENT_USER,
  DRIVER_REGION,
  MEMBER_PREFERENCE_SETTINGS,
  REFRESH_TOKEN,
} from '../properties/local-storage.properties';
import { isNull } from '../utils/util';
import { ILoggedInUserViewModel } from '../../model/common.model';
import { TimezoneInterface } from '@interfaces/timezone/timezone.interface';
import { UserPreference } from '../interfaces/user-preference.interface';
import { DriverRegionInterface } from '../../pages/v2/driver-scheduling/interfaces/driver-region.interface';

@Injectable({
  providedIn: 'root',
})
// todo functionality in progress
export class LocalStorageService {
  /**
   * set value to local storage
   * @param key   - local storage key
   * @param value - setting value
   */
  setValue(key: string, value: any): void {
    localStorage.setItem(key, value);
  }

  /**
   * get value from local storage
   * @param key - local storage key
   */
  getValue(key: string): string | null {
    return localStorage.getItem(key);
  }

  /**
   * check if local storage has value
   * @param key - local storage key
   */
  hasValue(key: string): boolean {
    return !isNull(localStorage.getItem(key));
  }

  /**
   * remove value fromo local storage
   * @param key   - local storage key
   */
  removeValue(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * set current user details
   * @param value - user details
   */
  setCurrentUserDetails(value: ILoggedInUserViewModel): void {
    // todo need to replace:
    // todo localStorage.setItem("user", JSON.stringify(value)) -> this.localStorageService.setCurrentUserDetails(value)
    localStorage.setItem(CURRENT_USER, JSON.stringify(value));
  }

  /**
   * get current user details
   */
  getCurrentUserDetails(): ILoggedInUserViewModel {
    // todo need to replace:
    // todo JSON.parse(localStorage.getItem("user")) -> this.localStorageService.getCurrentUserDetails()
    return this.hasValue(CURRENT_USER) ? JSON.parse(this.getValue(CURRENT_USER) ?? '') : null;
  }

  /**
   * check if current user logged in
   */
  isLoggedIn(): boolean {
    return !!this.getCurrentUserDetails();
  }

  /**
   * set access token details
   * @param value - access token value
   */
  setAccessToken(value: string): void {
    localStorage.setItem(ACCESS_TOKEN, JSON.stringify(value));
  }

  /**
   * get access token
   */
  getAccessToken(): string {
    // todo need to replace:
    // todo localStorage.getItem("AccessToken") -> this.localStorageService.getAccessToken()
    return this.hasValue(ACCESS_TOKEN) ? this.getValue(ACCESS_TOKEN ?? '') : null;
  }

  /**
   * set refresh token details
   * @param value - refresh token value
   */
  setRefreshToken(value: string): void {
    localStorage.setItem(REFRESH_TOKEN, JSON.stringify(value));
  }

  /**
   * get refresh token
   */
  getRefreshToken(): string {
    return this.hasValue(REFRESH_TOKEN) ? this.getValue(REFRESH_TOKEN) : null;
  }

  /**
   * get current user preference
   */
  getCurrentUserPreference(): UserPreference {
    return this.hasValue(MEMBER_PREFERENCE_SETTINGS) ? JSON.parse(this.getValue(MEMBER_PREFERENCE_SETTINGS) ?? '') : null;
  }

  /**
   * get current user role
   */
  getCurrentUserRole(): string {
    return this.getCurrentUserDetails()?.role;
  }

  /**
   * logout user
   */
  onLogout(): void {
    localStorage.removeItem(CURRENT_USER);
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
  }

  /**
   * Retrieves the stored timezone from the application's storage.
   *
   * @return {TimezoneInterface | null} The stored timezone as a TimezoneInterface object, or null if no timezone is stored.
   */
  getStoredTimezone(): TimezoneInterface | null {
    return JSON.parse(this.getValue(CURRENT_TIMEZONE));
  }

  /**
   * Sets the current timezone for the application.
   * @param timezone - timezone object to save in localstorage
   */
  setCurrentTimezone(timezone: TimezoneInterface): void {
    this.setValue(CURRENT_TIMEZONE, JSON.stringify(timezone));
  }

  /**
   * Retrieves the stored driver region id from the application's storage.
   *
   * @return {string} The stored driver region id, or null if no driver region id is stored.
   */
  getStoredDriverRegion(): DriverRegionInterface | null {
    return JSON.parse(this.getValue(DRIVER_REGION));
  }

  /**
   * Sets the selected driver region for the application.
   * @param driverRegion - driver region object to save in localstorage
   */
  setDriverRegion(driverRegion: DriverRegionInterface): void {
    this.setValue(DRIVER_REGION, JSON.stringify(driverRegion));
  }
}
